import type { BrandInsightsPageContext, Chat, ChatContextItem } from '@trinity/chat';
import { BiChatContextType, ChatContextType, resolveSubjectScopedKind } from '@trinity/chat';
import { generateEmptyChatSessionFiltersState } from '@trinity/chat-components/components/Chat.State/state.initial-state';
import { toTitleCase } from '@trinity/chat-components/utils';

export function calculateIfFiltersActive(data: CalculateActiveCount) {
  return calculateActiveFiltersCount(data) > 0;
}

export function calculateActiveFiltersCount({ context, data, features }: CalculateActiveCount) {

  let count = data.custom.length;
  const defaults = generateEmptyChatSessionFiltersState({ context, features });

  count += countStandardFilterDiffs(data, defaults, context);
  count += countEngagementFilter(data, features, context);
  count += countContextScopedFilter(context);

  return count;
}

/**
 * Project/Document/Folder/Brand/Transcript contexts scope the chat to a single entity
 * (via `context.data.subject` for Agent contexts) rather than `data.projects`/`data.records`.
 */
export function isSubjectScopedContext(context: BrandInsightsPageContext.Item): boolean {
  return resolveSubjectScopedKind(context) !== null;
}

const BI_IDENTIFIER_BY_CONTEXT_TYPE: Partial<Record<ChatContextType, string>> = {
  [ChatContextType.Project]: BiChatContextType.Project,
  [ChatContextType.Brand]: BiChatContextType.Brand,
  [ChatContextType.Folder]: BiChatContextType.Folder,
  [ChatContextType.Document]: BiChatContextType.Document,
  [ChatContextType.Transcript]: BiChatContextType.Transcript,
};

/**
 * The Context filter's value for the active context. Agent contexts carry their own context item;
 * a plain page context (Project/Brand/Folder/Document/Transcript, e.g. after picking a result in the
 * header search) maps to the selectable context item whose agent is that type, so the Context field
 * still names it. Returns null when no such item exists.
 */
export function resolveContextFilterValue(
  context: BrandInsightsPageContext.Item,
  contextItems: ChatContextItem[] | undefined
): { identifier: string; name: string } | null {
  if (context.type === ChatContextType.Agent) {
    return context.data ? { identifier: context.data.identifier, name: context.data.name } : null;
  }

  if (!isSubjectScopedContext(context) || !contextItems) {
    return null;
  }

  // Brand-insights context items are identified by their `bi-*` identifier (the same one the header
  // search and Agent contexts use); fall back to the item's agent identifier for other setups.
  const biIdentifier = BI_IDENTIFIER_BY_CONTEXT_TYPE[context.type];
  const matches = contextItems.filter(
    (item) => item.identifier === biIdentifier || item.agent.identifier === (context.type as string)
  );
  const match =
    matches.find((item) => item.identifier === biIdentifier) ??
    matches.find((item) => item.visibleInSelector) ??
    matches[0];

  if (match) {
    return { identifier: match.identifier, name: match.name };
  }

  // No configured item: still name the scope instead of leaving the field empty.
  return biIdentifier ? { identifier: biIdentifier, name: toTitleCase(context.type) } : null;
}

/**
 * Counts a subject-scoped context's selection as an active filter so the Source Filter
 * badge reflects it — see `isSubjectScopedContext`.
 */
function countContextScopedFilter(context: BrandInsightsPageContext.Item): number {
  if (isSubjectScopedContext(context)) {
    if (context.type === ChatContextType.Agent) {
      return context.data.subject != null ? 1 : 0;
    }
    // A direct (non-Agent) Project context uses the plain `filters.projects`
    // multi-select (see Project.tsx's `resolveMode`), which `countStandardFilterDiffs`
    // already counts — counting it again here would double-count the same selection.
    if (context.type === ChatContextType.Project) {
      return 0;
    }
    // Direct (non-Agent) Document/Folder/Brand/Transcript contexts always
    // carry a required entity id — there's no "unselected" state to guard,
    // so the context itself always represents an active scope.
    return 1;
  }

  return 0;
}

function sortedById(items: { id: string }[] | undefined | null) {
  return JSON.stringify((items || []).concat().sort((a, b) => a.id.localeCompare(b.id)));
}

function countStandardFilterDiffs(data: Chat.Filters, defaults: Chat.Filters, context: BrandInsightsPageContext.Item): number {
  let count = 0;
  if (sortedById(data.sources) != sortedById(defaults.sources)) count++;
  if (data.minDocumentDate != defaults.minDocumentDate) count++;
  if (data.tags.length) count++;
  if (sortedById(data.dataSources) != sortedById(defaults.dataSources)) count++;
  if (data.records?.length && [
    ChatContextType.Global,
    ChatContextType.KolProfile,
  ].includes(context.type)) {
    count += data.records.length;
  }
  if (data.projects.length) count++;
  return count;
}

function countEngagementFilter(data: Chat.Filters, features: CalculateActiveCount['features'], context: BrandInsightsPageContext.Item): number {
  if (features.engagementFilter && data.engagement?.id !== 'all' && [
    ChatContextType.Global,
    ChatContextType.KolProfile,
  ].includes(context.type)) {
    return 1;
  }
  return 0;
}

type CalculateActiveCount = {
  context: BrandInsightsPageContext.Item;
  data: Chat.Filters;
  features: {
    hcpProfiles: boolean;
    workspaces: boolean;
    engagementFilter: boolean;
  }
};
